import { useState } from 'react'

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-14 scroll-mt-6">
    <h2 className="text-base font-semibold text-white mb-5 pb-2 border-b border-[#2d2d2d] flex items-center gap-2">
      {title}
    </h2>
    {children}
  </section>
)

const SubSection = ({ id, title, children }) => (
  <div id={id} className="mb-8 scroll-mt-6">
    <h3 className="text-sm font-semibold text-[#cccccc] mb-3">{title}</h3>
    {children}
  </div>
)

const P = ({ children }) => (
  <p className="text-sm text-[#ababab] leading-relaxed mb-3">{children}</p>
)

const Code = ({ children }) => (
  <code className="font-mono text-xs bg-[#1e1e1e] text-[#ce9178] px-1.5 py-0.5 rounded border border-[#3c3c3c]">
    {children}
  </code>
)

const Badge = ({ color = 'blue', children }) => {
  const colors = {
    blue:   'bg-[#003f6b] text-[#6cb6f5] border-[#0078d4]',
    green:  'bg-[#1e3a1e] text-[#4ec994] border-[#4ec99450]',
    yellow: 'bg-[#3a2e00] text-[#ddb900] border-[#ddb90060]',
    red:    'bg-[#3a1e1e] text-[#f44747] border-[#f4474760]',
    gray:   'bg-[#2d2d2d] text-[#858585] border-[#3c3c3c]',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border ${colors[color]}`}>
      {children}
    </span>
  )
}

const Alert = ({ type = 'info', children }) => {
  const styles = {
    info:  { bar: 'bg-[#0078d4]', bg: 'bg-[#001f3a]', text: 'text-[#6cb6f5]', icon: 'ℹ' },
    tip:   { bar: 'bg-[#4ec994]', bg: 'bg-[#001a0f]', text: 'text-[#4ec994]',  icon: '✓' },
    warn:  { bar: 'bg-[#ddb900]', bg: 'bg-[#1a1400]', text: 'text-[#ddb900]',  icon: '⚠' },
  }
  const s = styles[type]
  return (
    <div className={`flex gap-3 ${s.bg} rounded p-3 mb-4 text-sm`}>
      <div className={`w-0.5 rounded-full ${s.bar} flex-shrink-0`} />
      <span className={`font-mono text-xs mt-0.5 flex-shrink-0 ${s.text}`}>{s.icon}</span>
      <p className="text-[#ababab] leading-relaxed">{children}</p>
    </div>
  )
}

const CodeBlock = ({ lang = 'python', label, children }) => (
  <div className="mb-4 rounded overflow-hidden border border-[#3c3c3c]">
    {label && (
      <div className="flex items-center gap-2 bg-[#2d2d2d] px-3 py-1.5 border-b border-[#3c3c3c]">
        {lang === 'python' && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M11.5 2C9.6 2 8 3.2 8 5v1h7v1H6C4 7 2 8.6 2 11.5S4 16 6 16h2v-2c0-2 1.5-3 3.5-3h5c1.7 0 3-1.3 3-3V5c0-1.8-1.6-3-3.5-3h-4.5z" fill="#3572A5"/>
            <path d="M12.5 22c1.9 0 3.5-1.2 3.5-3v-1H9v-1h9c2 0 4-1.6 4-4.5S20 8 18 8h-2v2c0 2-1.5 3-3.5 3h-5c-1.7 0-3 1.3-3 3v3c0 1.8 1.6 3 3.5 3h4.5z" fill="#FFD43B"/>
          </svg>
        )}
        <span className="text-xs text-[#858585] font-mono">{label}</span>
      </div>
    )}
    <pre className="bg-[#1e1e1e] text-xs text-[#d4d4d4] p-4 overflow-x-auto leading-5 font-mono whitespace-pre">
      <code>{children.trim()}</code>
    </pre>
  </div>
)

const PropTable = ({ rows }) => (
  <div className="mb-4 rounded overflow-hidden border border-[#3c3c3c] text-xs">
    <table className="w-full">
      <thead>
        <tr className="bg-[#2d2d2d] text-[#858585] uppercase tracking-wide text-[10px]">
          <th className="text-left px-3 py-2 font-medium">Campo</th>
          <th className="text-left px-3 py-2 font-medium">Tipo</th>
          <th className="text-left px-3 py-2 font-medium">Descrição</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#2d2d2d]">
        {rows.map(([field, type, desc], i) => (
          <tr key={i} className="bg-[#1e1e1e] hover:bg-[#252526]">
            <td className="px-3 py-2 font-mono text-[#9cdcfe]">{field}</td>
            <td className="px-3 py-2 text-[#4ec994]">{type}</td>
            <td className="px-3 py-2 text-[#ababab]">{desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------
const NAV = [
  { id: 'overview',     label: 'Visão Geral' },
  { id: 'anatomy',      label: 'Anatomia do Conector' },
  { id: 'naming',       label: 'Convenção de Nomenclatura' },
  { id: 'config',       label: 'Carregando Configurações' },
  { id: 'config-filters', label: '↳ Configurar Filtros de Integração' },
  { id: 'lifecycle',    label: 'Ciclo de Vida' },
  { id: 'process',      label: '↳ process — Método Principal' },
  { id: 'return',       label: 'Formato de Retorno' },
  { id: 'fieldmapping', label: 'Mapeamento de Campos' },
  { id: 'errors',       label: 'Tratamento de Erros' },
  { id: 'tracer',       label: 'Rastreamento (ConnectorTracer)' },
  { id: 'snippets',     label: 'Code Snippets' },
  { id: 'approval',     label: 'Fluxo de Aprovação' },
  { id: 'example',      label: 'Exemplo Completo' },
]

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------
export default function DocsModal({ onClose }) {
  const [activeSection, setActiveSection] = useState('overview')

  const scrollTo = (id) => {
    setActiveSection(id)
    document.getElementById(`doc-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-6xl h-[90vh] bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-[#252526] border-b border-[#3c3c3c] flex-shrink-0">
          <div className="flex items-center gap-3">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0078d4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            <span className="text-sm font-semibold text-white">Guia do Desenvolvedor</span>
            <span className="text-[#858585] text-xs">/ Conectores N4H</span>
          </div>
          <button
            onClick={onClose}
            className="text-[#858585] hover:text-white hover:bg-[#ffffff15] p-1.5 rounded transition"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">

          {/* Left nav */}
          <nav className="w-52 flex-shrink-0 bg-[#252526] border-r border-[#3c3c3c] overflow-y-auto py-4">
            <p className="px-4 text-[10px] font-semibold uppercase tracking-widest text-[#555] mb-2">Conteúdo</p>
            {NAV.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`w-full text-left px-4 py-1.5 text-xs transition rounded-none ${
                  activeSection === id
                    ? 'text-white bg-[#0078d420] border-l-2 border-[#0078d4]'
                    : 'text-[#858585] hover:text-[#cccccc] hover:bg-[#ffffff08] border-l-2 border-transparent'
                } ${label.startsWith('↳') ? 'pl-7' : ''}`}
              >
                {label}
              </button>
            ))}
          </nav>

          {/* Content */}
          <div
            className="flex-1 overflow-y-auto px-10 py-8"
            onScroll={(e) => {
              const container = e.currentTarget
              for (const { id } of [...NAV].reverse()) {
                const el = document.getElementById(`doc-${id}`)
                if (el && el.offsetTop - container.scrollTop <= 80) {
                  setActiveSection(id)
                  break
                }
              }
            }}
          >

            {/* ── Visão Geral ──────────────────────────────────────────── */}
            <div id="doc-overview">
              <Section id="doc-overview" title="Visão Geral">
                <P>
                  Um <strong className="text-[#cccccc]">conector</strong> é um módulo Python responsável por integrar
                  a plataforma N4H com um sistema externo. Ele recebe eventos CloudEvents do processador central,
                  transforma os dados conforme necessário e os entrega ao destino (uma API REST, um serviço DICOM, etc.).
                </P>
                <P>
                  Cada conector vive em um arquivo <Code>.py</Code> dedicado dentro do diretório{' '}
                  <Code>connectors/</Code> e segue um protocolo de interface simples — sem herança obrigatória,
                  apenas implementação dos métodos esperados (<em>duck typing</em>).
                </P>

                <div className="grid grid-cols-3 gap-3 my-5">
                  {[
                    { icon: '⚡', title: 'Assíncrono', desc: 'Todos os métodos de I/O são async/await' },
                    { icon: '🔌', title: 'Plug-and-play', desc: 'Carregado dinamicamente por nome de arquivo' },
                    { icon: '🔒', title: 'Isolado', desc: 'Configurações carregadas por empresa + integração' },
                  ].map(({ icon, title, desc }) => (
                    <div key={title} className="bg-[#252526] border border-[#3c3c3c] rounded p-3">
                      <p className="text-lg mb-1">{icon}</p>
                      <p className="text-xs font-semibold text-[#cccccc] mb-1">{title}</p>
                      <p className="text-xs text-[#858585] leading-relaxed">{desc}</p>
                    </div>
                  ))}
                </div>

                <Alert type="info">
                  O processador de eventos (<Code>cloudevent_processor.py</Code>) instancia automaticamente o conector
                  correto com base no campo <Code>connector_file</Code> da integração cadastrada no banco de dados.
                </Alert>
              </Section>
            </div>

            {/* ── Anatomia ─────────────────────────────────────────────── */}
            <div id="doc-anatomy">
              <Section id="doc-anatomy" title="Anatomia do Conector">
                <P>
                  Todo conector é uma classe Python com métodos principais. Nenhuma classe base precisa ser
                  herdada — a plataforma usa duck typing. Apenas <Code>__init__</Code> e <Code>process()</Code> são
                  obrigatórios.
                </P>
                <CodeBlock lang="python" label="estrutura_minima.py">
{`from typing import Any, Dict

class MeuSistemaConnector:
    """Connector simples para integração REST."""

    def __init__(self, config: Dict[str, Any]) -> None:
        """
        Inicializa o connector com config do processor.

        Args:
            config: Dict com:
                - integration_id: UUID da integração
                - company_id: UUID da empresa
                - tracer: ConnectorTracer (nunca é None, mesmo em dev portal)
        """
        self.integration_id = config.get("integration_id")
        self.company_id = config.get("company_id")
        self.tracer = config.get("tracer")  # Use self.tracer.success() / error()

    async def process(self, event_data: Dict[str, Any]) -> Dict[str, Any]:
        """
        Processa um CloudEvent.

        Args:
            event_data: Dict com id, type, source, data, etc.

        Returns:
            Dict com status="success"|"failed", message, e outros campos opcionais
        """
        try:
            # Registra sucesso
            self.tracer.success("process", {
                "event_type": event_data.get("type"),
            })

            return {
                "status": "success",
                "message": "Evento processado",
                "webhook_status_code": 200,
            }

        except Exception as exc:
            # Registra erro
            self.tracer.error("process", exc, {})
            return {
                "status": "failed",
                "message": str(exc),
                "webhook_status_code": None,
            }`}
                </CodeBlock>

                <Alert type="tip">
                  <strong>ConnectorTracer nunca é None!</strong> Diferente de implementações antigas,
                  <Code>self.tracer</Code> sempre é uma instância válida (EventTracer em produção,
                  SandboxTracer em dev portal). Você pode chamar <Code>self.tracer.success()</Code> diretamente
                  sem <Code>if self.tracer:</Code> checks.
                </Alert>
              </Section>
            </div>

            {/* ── Nomenclatura ─────────────────────────────────────────── */}
            <div id="doc-naming">
              <Section id="doc-naming" title="Convenção de Nomenclatura">
                <P>
                  A plataforma usa <strong className="text-[#cccccc]">importação dinâmica</strong> baseada no nome do
                  arquivo. O nome do arquivo em <Code>snake_case</Code> é convertido automaticamente para o nome da
                  classe em <Code>PascalCase</Code>.
                </P>
                <div className="bg-[#252526] border border-[#3c3c3c] rounded p-4 mb-4 font-mono text-xs">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[#858585]">arquivo</span>
                    <span className="text-[#ce9178]">meu_sistema_connector.py</span>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[#858585]">classe </span>
                    <span className="text-[#4ec994]">MeuSistemaConnector</span>
                  </div>
                  <div className="border-t border-[#3c3c3c] mt-3 pt-3 text-[#555] text-[11px]">
                    # regra: cada segmento separado por _ tem a primeira letra maiúscula
                  </div>
                </div>
                <PropTable rows={[
                  ['logic_connector.py',             '→', 'LogicConnector'],
                  ['logicmed_worklist_connector.py', '→', 'LogicmedWorklistConnector'],
                  ['meu_sistema_v2_connector.py',    '→', 'MeuSistemaV2Connector'],
                ]} />
                <Alert type="warn">
                  O nome da classe deve seguir <em>exatamente</em> essa convenção. Um nome incorreto causa
                  <Code>AttributeError</Code> em produção ao tentar carregar o conector.
                </Alert>
              </Section>
            </div>

            {/* ── Configurações ────────────────────────────────────────── */}
            <div id="doc-config">
              <Section id="doc-config" title="Carregando Configurações">
                <P>
                  O módulo <Code>connectors.config_helper</Code> fornece funções utilitárias para buscar parâmetros
                  registrados no banco de dados. Há dois escopos distintos:
                </P>

                <SubSection id="doc-config-integration" title="Parâmetros da Integração">
                  <P>
                    São parâmetros específicos da relação <strong className="text-[#cccccc]">empresa ↔ integração</strong>,
                    como URL da API, credenciais, tokens, etc. Cadastrados no admin em <em>Cadastros → Integrações → Parâmetros</em>.
                  </P>
                  <CodeBlock lang="python" label="config_helper.py — parâmetros da integração">
{`from connectors.config_helper import (
    get_all_integration_config_values,   # retorna dict com todos os valores
    get_integration_config_value,        # retorna um único valor por código
)

# ✅ Recomendado: busca todos em uma única query ao banco
cfg = get_all_integration_config_values(str(self.integration_id), str(self.company_id))
api_url  = cfg.get("api_url")       # None se não configurado
api_key  = cfg.get("api_key")       # None se não configurado

# Busca pontual (use apenas quando necessário — abre nova conexão a cada chamada)
webhook = get_integration_config_value(integration_id, company_id, "webhook_url")`}
                  </CodeBlock>
                </SubSection>

                <SubSection id="doc-config-company" title="Configurações Globais da Empresa">
                  <P>
                    São variáveis globais configuradas pela própria empresa no painel Admin → Configurações.
                    Úteis para valores compartilhados entre várias integrações, como <Code>BASE_URL</Code> do HIS.
                  </P>
                  <CodeBlock lang="python" label="config_helper.py — configurações da empresa">
{`from connectors.config_helper import (
    get_all_company_config_values,   # retorna dict com todas as configs (variáveis expandidas)
    get_company_config_value,        # retorna uma config pelo nome da chave
)

# Busca todas as configs globais da empresa em uma única query
company_cfg = get_all_company_config_values(str(self.company_id))
base_url    = company_cfg.get("BASE_URL")
his_tenant  = company_cfg.get("HIS_TENANT_ID")

# Busca pontual
base_url = get_company_config_value(str(self.company_id), "BASE_URL")`}
                  </CodeBlock>

                  <P>
                    <strong className="text-[#cccccc]">Suporte a Variáveis de Template:</strong> O método{' '}
                    <Code>get_all_company_config_values()</Code> suporta referências entre configurações
                    usando a sintaxe <Code>{"{{chave}}"}</Code>. As variáveis são expandidas automaticamente
                    durante o carregamento, permitindo reutilizar valores entre múltiplas configurações.
                  </P>

                  <div className="bg-[#252526] border border-[#3c3c3c] rounded p-3 mb-4">
                    <p className="text-xs font-mono text-[#9cdcfe] mb-2">Exemplo de configuração com variáveis:</p>
                    <div className="space-y-1 text-xs text-[#ababab] font-mono">
                      <div><span className="text-[#ce9178]">BASE_URL</span> = <span className="text-[#4ec994]">"https://api.example.com"</span></div>
                      <div><span className="text-[#ce9178]">API_PATH</span> = <span className="text-[#4ec994]">{"\"{{BASE_URL}}/v1/orders\""}</span></div>
                      <div><span className="text-[#ce9178]">WEBHOOK_URL</span> = <span className="text-[#4ec994]">{"\"{{API_PATH}}/webhooks\""}</span></div>
                    </div>
                  </div>

                  <CodeBlock lang="python" label="variáveis são expandidas automaticamente">
{`# No banco de dados:
# - BASE_URL = "https://api.example.com"
# - API_PATH = "{{BASE_URL}}/v1/orders"

# Ao carregar no conector:
cfg = get_all_company_config_values(str(self.company_id))

# As variáveis são automaticamente expandidas:
cfg["BASE_URL"]   # "https://api.example.com"
cfg["API_PATH"]   # "https://api.example.com/v1/orders" ({{BASE_URL}} foi substituído)
cfg["WEBHOOK_URL"] # "https://api.example.com/v1/orders/webhooks" (ambas as variáveis foram expandidas)`}
                  </CodeBlock>

                  <Alert type="tip">
                    <strong>Expansão Recursiva:</strong> As variáveis são expandidas recursivamente, permitindo
                    que <Code>{"{{BASE_URL}}"}</Code> seja expandido para seu valor, e então esse valor seja usado
                    em outras expansões. Referências circulares são automaticamente detectadas e prevenidas.
                  </Alert>

                  <Alert type="info">
                    Sempre use <Code>get_all_company_config_values()</Code> em vez de chamadas pontuais quando
                    precisar de múltiplas configurações — é mais eficiente e garante que todas as expansões
                    de variáveis sejam processadas consistentemente.
                  </Alert>
                </SubSection>

                <Alert type="tip">
                  Prefira sempre <Code>get_all_*</Code> no <Code>__init__</Code> para carregar todos os parâmetros
                  de uma vez. Chamadas pontuais abrem uma nova sessão de banco a cada invocação.
                </Alert>

                <SubSection id="doc-config-filters" title="Configurar Filtros de Integração">
                  <P>
                    A plataforma permite configurar <strong className="text-[#cccccc]">rótulos personalizados</strong> para
                    campos de filtro específicos de uma integração. Quando os filtros são consultados via API, a
                    plataforma retorna tanto o caminho técnico do campo (<Code>filter_field</Code>) quanto seu
                    rótulo amigável (<Code>filter_label</Code>).
                  </P>
                  <P>
                    Isso é útil para:
                  </P>
                  <ul className="space-y-1 mb-4 text-sm text-[#ababab] ml-4">
                    <li>• Exibir nomes descritivos nos filtros do painel admin</li>
                    <li>• Documentar os campos disponíveis de forma legível</li>
                    <li>• Facilitar a configuração de filtros para usuários não-técnicos</li>
                  </ul>

                  <CodeBlock lang="python" label="config_helper.py — configure_integration_filter_fields">
{`from connectors.config_helper import configure_integration_filter_fields

# Configura os campos de filtro para esta integração + empresa.
# Cada chamada sincroniza a configuração com este dicionário: campos novos
# são criados, campos existentes têm o label atualizado, e campos que somem
# do dicionário são removidos (ou desativados, se já tiverem eventos
# processados com valores gravados para eles — veja o Alert abaixo).
filter_fields = {
    "data.idUnidade": "ID da Unidade",
    "data.specialty": "Especialidade",
    "patient_id": "ID do Paciente",
    "exam_type": "Tipo de Exame",
}

success = configure_integration_filter_fields(
    integration_id=str(self.integration_id),
    company_id=str(self.company_id),
    filter_fields=filter_fields
)

if success:
    self.tracer.success("configure_filters", {
        "integration_id": self.integration_id,
        "company_id": self.company_id,
        "fields_configured": len(filter_fields),
    })
else:
    self.tracer.error("configure_filters",
        Exception("Falha ao configurar filtros"),
        {"fields": list(filter_fields.keys())}
    )`}
                  </CodeBlock>

                  <P>
                    <strong className="text-[#cccccc]">Estrutura do Dicionário:</strong> Mapa de{' '}
                    <Code>{"filter_field: filter_label"}</Code> onde:
                  </P>
                  <div className="bg-[#252526] border border-[#3c3c3c] rounded p-3 mb-4">
                    <div className="space-y-2 text-xs font-mono">
                      <div><span className="text-[#9cdcfe]">filter_field</span> <span className="text-[#ababab]">(str)</span> — Caminho técnico do campo, ex: <span className="text-[#ce9178]">"data.idUnidade"</span> ou <span className="text-[#ce9178]">"patient_id"</span></div>
                      <div><span className="text-[#9cdcfe]">filter_label</span> <span className="text-[#ababab]">(str)</span> — Rótulo amigável, ex: <span className="text-[#ce9178]">"ID da Unidade"</span> ou <span className="text-[#ce9178]">"ID do Paciente"</span></div>
                    </div>
                  </div>

                  <P>
                    <strong className="text-[#cccccc]">Comportamento importante:</strong> cada chamada a{' '}
                    <Code>configure_integration_filter_fields()</Code> sincroniza a configuração com o dicionário
                    informado — campos presentes são criados/atualizados, campos ausentes são removidos. A única
                    exceção é um campo que a plataforma já usou para gravar valores de eventos processados: nesse
                    caso ele não é apagado (isso quebraria a busca por esses valores), apenas marcado como inativo
                    (deixa de aparecer em <Code>/api/data-filters/schema</Code> e nos filtros do painel). Se esse
                    mesmo campo voltar a aparecer em uma chamada futura, ele é reativado normalmente.
                  </P>

                  <CodeBlock lang="python" label="resposta da API /data-filters/schema — com labels configurados">
{`# GET /api/data-filters/schema?integration_id=uuid&company_id=uuid

{
  "integration_id": "123e4567-e89b-12d3-a456-426614174000",
  "fields": [
    {
      "path": "data.idUnidade",
      "type": "text",
      "label": "ID da Unidade"  // Configurado via configure_integration_filter_fields
    },
    {
      "path": "data.specialty",
      "type": "text",
      "label": "Especialidade"  // Configurado
    },
    {
      "path": "other_field",
      "type": "text",
      "label": null  // Não configurado — retorna null
    }
  ]
}`}
                  </CodeBlock>

                  <Alert type="info">
                    A função <Code>configure_integration_filter_fields()</Code> é idempotente — pode ser chamada
                    múltiplas vezes com segurança. Cada chamada sincroniza os filtros da empresa+integração com o
                    dicionário informado, preservando (como inativos) os campos que já têm valores de eventos
                    gravados, mesmo que deixem de ser anunciados.
                  </Alert>

                  <Alert type="tip">
                    Use esta funcionalidade no método <Code>__init__</Code> ou em um webhook de setup para garantir
                    que os filtros sejam configurados quando a integração é ativada para uma nova empresa.
                  </Alert>
                </SubSection>
              </Section>
            </div>

            {/* ── Ciclo de Vida ────────────────────────────────────────── */}
            <div id="doc-lifecycle">
              <Section id="doc-lifecycle" title="Ciclo de Vida">
                <div className="flex items-center gap-2 flex-wrap mb-5">
                  {['__init__', '→', 'process()', '→', 'Seu código', '→', 'return'].map((s, i) => (
                    s === '→'
                      ? <span key={i} className="text-[#555] text-sm">→</span>
                      : <Badge key={i} color={s === 'Seu código' ? 'yellow' : s === 'return' ? 'green' : 'blue'}>{s}</Badge>
                  ))}
                </div>
                <P>
                  Quando um evento chega, o processador:
                </P>
                <div className="space-y-2 mb-4 text-sm text-[#ababab]">
                  <div className="flex gap-2">
                    <span className="font-mono text-[#9cdcfe] flex-shrink-0">1.</span>
                    <span>Instancia seu conector chamando <Code>__init__(config)</Code></span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-mono text-[#9cdcfe] flex-shrink-0">2.</span>
                    <span>Chama <Code>await process(event_data)</Code></span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-mono text-[#9cdcfe] flex-shrink-0">3.</span>
                    <span>Aguarda o resultado (dict com status, message, etc.)</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-mono text-[#9cdcfe] flex-shrink-0">4.</span>
                    <span>Salva todos os dados no banco para o painel de eventos</span>
                  </div>
                </div>
                <Alert type="tip">
                  <strong>Apenas <Code>process()</Code> é obrigatório!</strong> Tudo que você precisa implementar
                  está em um único método async. Transformação, validação, HTTP — tudo lá com rastreamento
                  simplificado via <Code>self.tracer</Code>.
                </Alert>
              </Section>
            </div>

            {/* ── process ──────────────────────────────────────────────── */}
            <div id="doc-process">
              <Section id="doc-process" title="process — Método Principal">
                <P>
                  Método <Code>async</Code> obrigatório que processa um CloudEvent. Aqui você implementa toda
                  a lógica: validação, transformação, chamadas HTTP, e retorna um dict com status.
                </P>
                <P>
                  Use <Code>self.tracer.success()</Code> e <Code>self.tracer.error()</Code> para registrar
                  etapas do processamento — todos os dados aparecem no painel de eventos!
                </P>
                <CodeBlock lang="python" label="process — estrutura padrão simplificada">
{`import httpx

async def process(self, event_data: dict) -> dict:
    """
    Processa um CloudEvent.

    Args:
        event_data: Dict com id, type, source, data, etc.

    Returns:
        Dict com status ("success" | "failed"), message, webhook_status_code, etc.
    """
    try:
        # 1. Validar entrada
        self.tracer.success("validate_input", {
            "event_type": event_data.get("type"),
        })

        # 2. Extrair dados
        data = event_data.get("data", {})
        if not data:
            self.tracer.skip("process_data", "Empty payload")
            return {
                "status": "success",
                "message": "Empty payload, skipped",
                "webhook_status_code": 200,
            }

        # 3. Chamar API externa com rastreamento automático
        async with httpx.AsyncClient(timeout=10.0) as client:
            response = await client.post(
                self.api_url,
                json=data,
                auth=(self.api_username, self.api_password),
            )

        if response.is_success:
            self.tracer.success("post_to_api", {
                "status_code": response.status_code,
                "response_size": len(response.content),
            })
            return {
                "status": "success",
                "message": "Event delivered successfully",
                "webhook_status_code": response.status_code,
            }
        else:
            self.tracer.error("post_to_api",
                Exception(f"HTTP {response.status_code}"),
                {"response_body": response.text[:500]}
            )
            return {
                "status": "failed",
                "message": f"API returned {response.status_code}",
                "webhook_status_code": response.status_code,
            }

    except Exception as exc:
        self.tracer.error("process", exc, {})
        return {
            "status": "failed",
            "message": str(exc),
            "webhook_status_code": None,
        }`}
                </CodeBlock>

                <SubSection id="doc-process-tips" title="Dicas Importantes">
                  <div className="space-y-2 text-sm">
                    <div className="flex gap-2">
                      <span className="text-[#4ec994] flex-shrink-0">✓</span>
                      <span className="text-[#ababab]">Sempre retorne um <Code>dict</Code> com pelo menos
                        <Code>status</Code> e <Code>message</Code></span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-[#4ec994] flex-shrink-0">✓</span>
                      <span className="text-[#ababab]">Use <Code>self.tracer.success()/error()/skip()</Code> para registrar
                        etapas — todos os dados ficam no banco</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-[#4ec994] flex-shrink-0">✓</span>
                      <span className="text-[#ababab]">Qualquer campo extra que você adicionar ao dict de retorno será
                        salvo no banco também!</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-[#f44747] flex-shrink-0">✗</span>
                      <span className="text-[#ababab]">Não guarde senhas, tokens ou dados sensíveis em metadata — eles
                        aparecem no painel de eventos</span>
                    </div>
                  </div>
                </SubSection>
              </Section>
            </div>

            {/* ── Formato de Retorno ───────────────────────────────────── */}
            <div id="doc-return">
              <Section id="doc-return" title="Formato de Retorno">
                <P>
                  O método <Code>process()</Code> deve retornar um <Code>dict</Code> com pelo menos os campos
                  abaixo. <strong>Qualquer campo adicional é automaticamente salvo no banco!</strong>
                </P>
                <PropTable rows={[
                  ['status',          'str',  '"success" | "error" | "rejected"'],
                  ['message',         'str',  'Descrição legível do resultado'],
                  ['api_url',         'str?', 'URL da API chamada (quando aplicável)'],
                  ['api_status_code', 'int?', 'Código HTTP retornado pela API externa'],
                  ['api_response',    'str?', 'Corpo da resposta da API externa'],
                  ['missing_fields',  'list?','Campos ausentes (erros de validação)'],
                  ['data',            'dict?','Payload processado (opcional)'],
                ]} />
                <CodeBlock lang="python" label="exemplos de retorno">
{`# Sucesso
{"status": "success", "message": "Evento entregue", "api_status_code": 200}

# Erro de validação (before_process)
{"status": "error",   "message": "Campos obrigatórios ausentes",
 "missing_fields": ["patientID", "accessionNumber"], "api_status_code": 400}

# Erro de conexão
{"status": "error",   "message": "Falha na conexão: ConnectTimeout", "api_url": "..."}

# Evento rejeitado (fora dos filtros da integração)
{"status": "rejected", "message": "Evento não corresponde aos filtros da integração"}`}
                </CodeBlock>
              </Section>
            </div>

            {/* ── Mapeamento de Campos ─────────────────────────────────── */}
            <div id="doc-fieldmapping">
              <Section id="doc-fieldmapping" title="Mapeamento de Campos (FIELD_SPEC)">
                <P>
                  Para integrações com muitos campos, o padrão <Code>FIELD_SPEC</Code> torna o mapeamento
                  declarativo, legível e fácil de manter. Cada entrada da lista define um campo destino,
                  se é obrigatório e de onde extrair o valor no payload (notação de ponto).
                </P>
                <CodeBlock lang="python" label="padrão FIELD_SPEC — exemplo real">
{`_BASE = "agreement.order"

FIELD_SPEC = [
    # campo destino            obrigatório   caminho no payload (dot-notation)
    {"field": "accessionNumber", "required": True,  "mapping": f"{_BASE}.requestedProcedure.accessionNumber"},
    {"field": "patientName",     "required": True,  "mapping": f"{_BASE}.patient.name"},
    {"field": "patientAge",      "required": False, "mapping": f"{_BASE}.patient.birthDate"},  # calculado
    {"field": "patientComments", "required": False, "mapping": None},                          # sempre None
    {
        # Campo composto: concatena council + state + id
        "field": "referringPhysicianCouncil",
        "required": True,
        "mapping": [
            f"{_BASE}.requestedProcedure.requesters.council",
            f"{_BASE}.requestedProcedure.requesters.councilState",
            f"{_BASE}.requestedProcedure.requesters.councilId",
        ],
    },
]`}
                </CodeBlock>
                <CodeBlock lang="python" label="helper _get_nested — resolve dot-notation em dicts aninhados">
{`from typing import Any

def _get_nested(self, data: dict, path: str) -> Any:
    """
    Resolve um caminho dot-notation num dict aninhado.
    Quando encontra uma lista no meio do caminho, usa o primeiro elemento.
    """
    current = data
    for key in path.split("."):
        if isinstance(current, list):
            current = current[0] if current else None
        if not isinstance(current, dict):
            return None
        current = current.get(key)
        if current is None:
            return None
    if isinstance(current, list):
        return current[0] if current else None
    return current`}
                </CodeBlock>
              </Section>
            </div>

            {/* ── Tratamento de Erros ──────────────────────────────────── */}
            <div id="doc-errors">
              <Section id="doc-errors" title="Tratamento de Erros">
                <P>
                  Use sempre <Code>try/except</Code> ao redor de chamadas HTTP e registre erros com{' '}
                  <Code>logger.error</Code>. O conector <strong className="text-[#cccccc]">nunca deve lançar
                  exceções não tratadas</strong> — o processador de eventos espera sempre um dict como retorno.
                </P>
                <CodeBlock lang="python" label="padrão de tratamento de erros">
{`import logging
import httpx

logger = logging.getLogger(__name__)

# No topo do arquivo — captura o nome do módulo automaticamente


# Dentro do método process:
try:
    async with httpx.AsyncClient(timeout=30.0) as client:
        response = await client.post(self.api_url, json=data)

    if not response.is_success:
        logger.error(
            f"[MeuSistema] API retornou {response.status_code} | "
            f"url={self.api_url} | body={response.text[:200]}"
        )
        return {
            "status": "error",
            "message": f"API retornou status {response.status_code}",
            "api_status_code": response.status_code,
            "api_response": response.text,
        }

except httpx.TimeoutException as exc:
    logger.error(f"[MeuSistema] Timeout ao conectar em {self.api_url}: {exc}")
    return {"status": "error", "message": f"Timeout: {exc}", "api_url": self.api_url}

except httpx.RequestError as exc:
    logger.error(f"[MeuSistema] Erro de rede: {exc}")
    return {"status": "error", "message": f"Erro de rede: {exc}", "api_url": self.api_url}

except Exception as exc:
    logger.exception(f"[MeuSistema] Erro inesperado: {exc}")
    return {"status": "error", "message": f"Erro inesperado: {exc}"}`}
                </CodeBlock>
              </Section>
            </div>

            {/* ── Tracer ───────────────────────────────────────────────── */}
            <div id="doc-tracer">
              <Section id="doc-tracer" title="Rastreamento com ConnectorTracer">
                <P>
                  O objeto <Code>self.tracer</Code> (passado via <Code>config</Code>) é uma instância de
                  <Code>ConnectorTracer</Code> que oferece uma interface simplificada para registrar etapas
                  do processamento. Todos os dados registrados aparecem no painel de eventos para auditoria
                  e debugging. <strong>Não precisa verificar se é None</strong> — a abstração já cuida disso!
                </P>

                <SubSection id="doc-tracer-basic" title="Uso Básico (Recomendado)">
                  <P>
                    <Code>ConnectorTracer</Code> fornece 3 métodos simples: <Code>success()</Code>,
                    <Code>error()</Code> e <Code>skip()</Code>. Nenhum try/except necessário!
                  </P>
                  <CodeBlock lang="python" label="padrão recomendado — limpo e DRY">
{`async def process(self, event_data: dict) -> dict:
    try:
        # Registra uma etapa bem-sucedida
        self.tracer.success("validate_input", {
            "event_type": event_data.get("type"),
            "company_id": self.company_id,
        })

        # Processa os dados
        data = event_data.get("data", {})

        # Registra chamada HTTP
        async with httpx.AsyncClient(timeout=10.0) as client:
            response = await client.post(
                self.api_url,
                json=data,
                timeout=10.0
            )

        # Sucesso ou erro automático baseado no status
        if response.is_success:
            self.tracer.success("post_to_api", {
                "status_code": response.status_code,
                "response_size": len(response.content),
            })
            return {
                "status": "success",
                "message": "OK",
                "webhook_status_code": response.status_code,
            }
        else:
            self.tracer.error("post_to_api",
                Exception(f"HTTP {response.status_code}"),
                {"response_body": response.text[:500]}
            )
            return {
                "status": "failed",
                "message": f"API returned {response.status_code}",
                "webhook_status_code": response.status_code,
            }

    except Exception as exc:
        # Registra erro (tracer cuida de try/except internamente)
        self.tracer.error("process", exc, {})
        return {
            "status": "failed",
            "message": str(exc),
            "webhook_status_code": None,
        }`}
                  </CodeBlock>
                </SubSection>

                <SubSection id="doc-tracer-methods" title="Métodos Disponíveis">
                  <div className="space-y-3">
                    <div className="bg-[#252526] border border-[#3c3c3c] rounded p-3">
                      <p className="font-mono text-xs text-[#9cdcfe] mb-2">
                        success(step_name: str, metadata: dict) → None
                      </p>
                      <p className="text-xs text-[#ababab]">
                        Registra uma etapa bem-sucedida. <Code>metadata</Code> é um dict com qualquer dado
                        relevante que será salvo no trace.
                      </p>
                    </div>

                    <div className="bg-[#252526] border border-[#3c3c3c] rounded p-3">
                      <p className="font-mono text-xs text-[#9cdcfe] mb-2">
                        error(step_name: str, exception: Exception, metadata: dict) → None
                      </p>
                      <p className="text-xs text-[#ababab]">
                        Registra uma etapa que falhou com uma exceção. O traceback é capturado automaticamente.
                      </p>
                    </div>

                    <div className="bg-[#252526] border border-[#3c3c3c] rounded p-3">
                      <p className="font-mono text-xs text-[#9cdcfe] mb-2">
                        skip(step_name: str, reason: str) → None
                      </p>
                      <p className="text-xs text-[#ababab]">
                        Marca uma etapa como pulada. Útil para lógica condicional.
                      </p>
                    </div>
                  </div>

                  <CodeBlock lang="python" label="exemplos de cada método">
{`# Success - etapa completada
self.tracer.success("validate_input", {
    "fields_found": 5,
    "valid": True,
})

# Error - exceção registrada automaticamente com traceback
try:
    result = call_external_api()
except Exception as e:
    self.tracer.error("call_api", e, {
        "endpoint": "https://api.example.com",
        "timeout": 10.0,
    })

# Skip - etapa pulada por lógica condicional
if not has_pdf:
    self.tracer.skip("process_pdf", "No PDF in payload")`}
                  </CodeBlock>
                </SubSection>

                <Alert type="tip">
                  <strong>Por que ConnectorTracer?</strong> A abstração elimina boilerplate (if tracer:, try/except manual,
                  etc). Você concentra-se na lógica, e o tracer cuida do registro. Funciona
                  em produção (EventTracer real) e no dev portal (mock tracer) automaticamente!
                </Alert>

                <Alert type="warn">
                  <strong>Dados Sensíveis:</strong> Qualquer dado que você passa para
                  <Code>metadata</Code> é registrado no banco. Evite senhas, tokens ou dados pessoais.
                </Alert>
              </Section>
            </div>

            {/* ── Code Snippets ───────────────────────────────────── */}
            <div id="doc-snippets">
              <Section id="doc-snippets" title="Code Snippets">
                <P>
                  A modal de <Code>Code Snippets</Code> fornece trechos de código prontos para uso ao trabalhar com conectores. Acelera o desenvolvimento com exemplos para chamadas HTTP, rastreamento e outras operações comuns.
                </P>

                <SubSection id="doc-snippets-access" title="Como Acessar">
                  <P>
                    Existem duas formas de abrir a modal de snippets:
                  </P>
                  <ul className="space-y-2 mb-4 text-sm text-[#ababab]">
                    <li>• <Badge color="blue">Ctrl+K</Badge> (ou <Badge color="blue">Cmd+K</Badge> no Mac) — Atalho de teclado direto no editor</li>
                    <li>• Clique no botão <Code>&lt;/&gt; Snippets</Code> na barra de ferramentas</li>
                  </ul>
                  <Alert type="tip">
                    O botão de snippets só funciona quando há um arquivo aberto. O atalho <Badge color="blue">Ctrl+K</Badge> funciona quando o cursor está dentro do editor.
                  </Alert>
                </SubSection>

                <SubSection id="doc-snippets-groups" title="Grupos de Snippets">
                  <P>
                    Os snippets são organizados em grupos para facilitar a navegação. Na esquerda da modal, clique em um grupo para filtrar:
                  </P>
                  <div className="space-y-3 mb-4">
                    <div className="bg-[#252526] border border-[#3c3c3c] rounded p-3">
                      <p className="text-xs font-mono text-[#0078d4] mb-1">All</p>
                      <p className="text-xs text-[#ababab]">Mostra todos os snippets disponíveis</p>
                    </div>
                    <div className="bg-[#252526] border border-[#3c3c3c] rounded p-3">
                      <p className="text-xs font-mono text-[#0078d4] mb-1">Tracer</p>
                      <p className="text-xs text-[#ababab]">Snippets para rastreamento: success, skip, error</p>
                    </div>
                    <div className="bg-[#252526] border border-[#3c3c3c] rounded p-3">
                      <p className="text-xs font-mono text-[#0078d4] mb-1">HTTP</p>
                      <p className="text-xs text-[#ababab]">Snippets para chamadas HTTP: GET, POST, PUT, PATCH, DELETE</p>
                    </div>
                    <div className="bg-[#252526] border border-[#3c3c3c] rounded p-3">
                      <p className="text-xs font-mono text-[#0078d4] mb-1">Native</p>
                      <p className="text-xs text-[#ababab]">Snippets de Python e framework (em expansão)</p>
                    </div>
                  </div>
                </SubSection>

                <SubSection id="doc-snippets-usage" title="Como Usar">
                  <ol className="space-y-2 mb-4 text-sm text-[#ababab]">
                    <li>1. Abra a modal com <Badge color="blue">Ctrl+K</Badge></li>
                    <li>2. Digite para buscar ou clique em um grupo para filtrar</li>
                    <li>3. Clique no botão <Code>🔽</Code> para visualizar o código (expandir preview)</li>
                    <li>4. Clique no botão <Code>➜</Code> para inserir o snippet no editor</li>
                    <li>5. A indentação se ajusta automaticamente ao contexto</li>
                  </ol>
                  <Alert type="tip">
                    Use os atalhos de teclado: <Badge color="blue">↑↓</Badge> para navegar, <Badge color="blue">Enter</Badge> para inserir, <Badge color="blue">Esc</Badge> para fechar.
                  </Alert>
                </SubSection>

                <SubSection id="doc-snippets-examples" title="Exemplos de Snippets">
                  <P>
                    <strong>Tracer Success:</strong> Registra sucesso de uma operação
                  </P>
                  <CodeBlock lang="python" label="Tracer Success">{`self.tracer.success("{step_name}", {
    "event_type": event_data.get("type"),
    "company_id": self.company_id,
})`}</CodeBlock>

                  <P>
                    <strong>HTTP GET:</strong> Realiza uma chamada GET com tratamento de erro
                  </P>
                  <CodeBlock lang="python" label="HTTP GET">{`import httpx

try:
    async with httpx.AsyncClient(timeout=30) as client:
        response = await client.get(
            "{api_url}",
            headers={"Authorization": f"Bearer {token}"},
            params={"param": "value"}
        )
        response.raise_for_status()
        data = response.json()
        self.tracer.success("get_request", {"status": response.status_code})
except httpx.HTTPError as e:
    self.tracer.error("get_request", {"error": str(e)})
    raise`}</CodeBlock>

                  <Alert type="info">
                    Clique no grupo <Code>HTTP</Code> para ver snippets de POST, PUT, PATCH e DELETE.
                  </Alert>
                </SubSection>

                <SubSection id="doc-snippets-search" title="Busca">
                  <P>
                    Use a barra de busca no topo para localizar snippets por:
                  </P>
                  <ul className="space-y-2 mb-4 text-sm text-[#ababab]">
                    <li>• <Code>success</Code> — encontra o snippet Tracer Success</li>
                    <li>• <Code>get</Code> — encontra o snippet HTTP GET</li>
                    <li>• <Code>api</Code> — encontra todos os snippets relacionados a API</li>
                    <li>• <Code>logging</Code> — encontra snippets de logging</li>
                  </ul>
                  <P>
                    A busca funciona no nome, descrição e tags de cada snippet.
                  </P>
                </SubSection>
              </Section>
            </div>

            {/* ── Fluxo de Aprovação ───────────────────────────────────── */}
            <div id="doc-approval">
              <Section id="doc-approval" title="Fluxo de Aprovação">
                <P>
                  Novos conectores passam por um fluxo de revisão antes de serem ativados em produção.
                </P>
                <div className="space-y-2 mb-5">
                  {[
                    { step: '1', badge: 'gray',  label: 'Upload',   desc: 'Faça upload do arquivo .py. O conector é salvo no banco com approved=False.' },
                    { step: '2', badge: 'blue',  label: 'Testes',   desc: 'Use o botão Run para testar com payloads reais. Corrija e salve quantas vezes precisar.' },
                    { step: '3', badge: 'yellow',label: 'Aprovação',desc: 'Um usuário com permissão de aprovação ativa o conector. O arquivo é gravado em connectors/.' },
                    { step: '4', badge: 'green', label: 'Produção', desc: 'A integração pode ser configurada para usar o connector_file. Eventos reais são processados.' },
                  ].map(({ step, badge, label, desc }) => (
                    <div key={step} className="flex items-start gap-3 bg-[#252526] border border-[#3c3c3c] rounded p-3">
                      <span className="w-5 h-5 rounded-full bg-[#3c3c3c] text-[#858585] text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{step}</span>
                      <div>
                        <Badge color={badge}>{label}</Badge>
                        <p className="text-xs text-[#ababab] mt-1 leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Alert type="warn">
                  Revogar a aprovação (<em>unapprove</em>) não remove o arquivo do disco imediatamente,
                  mas eventos novos deixarão de ser processados pelo conector até que ele seja reaprovado.
                </Alert>
              </Section>
            </div>

            {/* ── Exemplo Completo ─────────────────────────────────────── */}
            <div id="doc-example">
              <Section id="doc-example" title="Exemplo Completo">
                <P>
                  Conector completo e funcional para integração com uma API REST hipotética.
                  Inclui carregamento de config, mapeamento de campos, chamada HTTP, tratamento de erros e rastreamento.
                </P>
                <CodeBlock lang="python" label="meu_sistema_connector.py">
{`"""
MeuSistema Connector - Encaminha eventos de worklist para a API do MeuSistema.

Suporta os tipos de evento:
  - worklist.create  → POST /worklist
  - worklist.update  → PATCH /worklist/{id}
  - worklist.delete  → DELETE /worklist/{id}
"""

import logging
from typing import Any

import httpx

from connectors.config_helper import (
    get_all_integration_config_values,
    get_all_company_config_values,
)

logger = logging.getLogger(__name__)

# Mapeamento declarativo: campo_destino → caminho_no_payload (dot-notation)
_BASE = "agreement.order"

FIELD_SPEC = [
    {"field": "externalId",   "required": True,  "mapping": f"{_BASE}.requestedProcedure.accessionNumber"},
    {"field": "patientId",    "required": True,  "mapping": f"{_BASE}.patient.id"},
    {"field": "patientName",  "required": True,  "mapping": f"{_BASE}.patient.name"},
    {"field": "examCode",     "required": True,  "mapping": f"{_BASE}.requestedProcedure.procedureId"},
    {"field": "modality",     "required": True,  "mapping": f"{_BASE}.requestedProcedure.modality"},
    {"field": "notes",        "required": False, "mapping": f"{_BASE}.observation"},
]

EVENT_METHODS = {
    "worklist.create": "POST",
    "worklist.update": "PATCH",
    "worklist.delete": "DELETE",
}


class MeuSistemaConnector:
    """Connector que encaminha eventos de worklist para a API do MeuSistema."""

    def __init__(self, config: dict = None):
        self.config = config or {}
        self.integration_id = self.config.get("integration_id")
        self.company_id     = self.config.get("company_id")
        self.tracer         = self.config.get("tracer")

        # Parâmetros específicos da integração
        cfg = get_all_integration_config_values(str(self.integration_id), str(self.company_id))
        self.api_url      = cfg.get("api_url")      or ""
        self.api_username = cfg.get("api_username") or ""
        self.api_password = cfg.get("api_password") or ""

        # Configurações globais da empresa (opcional)
        company_cfg    = get_all_company_config_values(str(self.company_id))
        self.tenant_id = company_cfg.get("TENANT_ID") or ""

    # ── Helpers ────────────────────────────────────────────────────────────

    def _get_nested(self, data: dict, path: str) -> Any:
        current = data
        for key in path.split("."):
            if isinstance(current, list):
                current = current[0] if current else None
            if not isinstance(current, dict):
                return None
            current = current.get(key)
            if current is None:
                return None
        return current[0] if isinstance(current, list) else current

    # ── Ciclo de vida ──────────────────────────────────────────────────────

    async def before_process(self, payload: dict) -> dict:
        """Mapeia o payload interno para o formato esperado pela API do MeuSistema."""
        data   = payload.get("data") or {}
        mapped = {}

        for spec in FIELD_SPEC:
            mapping = spec.get("mapping")
            mapped[spec["field"]] = self._get_nested(data, mapping) if mapping else None

        # Campos obrigatórios
        missing = [s["field"] for s in FIELD_SPEC if s["required"] and not mapped.get(s["field"])]
        if missing:
            logger.error(f"[MeuSistema] Campos obrigatórios ausentes: {missing}")
            return {
                "status": "error",
                "message": "Campos obrigatórios ausentes",
                "missing_fields": missing,
                "api_status_code": 400,
            }

        # Injeta tenant da empresa no payload destino
        if self.tenant_id:
            mapped["tenantId"] = self.tenant_id

        return {**payload, "data": mapped}

    async def process(self, payload: dict) -> dict:
        """Envia o evento transformado para a API do MeuSistema com rastreamento simplificado."""
        try:
            # 1. Transformar e validar
            result = await self.before_process(payload)
            if result.get("status") == "error":
                self.tracer.error("validate_payload", Exception(result.get("message")), {
                    "missing_fields": result.get("missing_fields"),
                })
                return result
            payload = result

            # 2. Verificar configurações
            if not self.api_url:
                self.tracer.error("check_config", Exception("api_url não configurada"), {})
                return {"status": "error", "message": "api_url não configurada"}
            if not self.api_username or not self.api_password:
                self.tracer.error("check_config", Exception("Credenciais não configuradas"), {})
                return {"status": "error", "message": "Credenciais de API não configuradas"}

            self.tracer.success("check_config", {"api_url": self.api_url})

            # 3. Selecionar método HTTP
            event_type  = payload.get("type", "")
            http_method = EVENT_METHODS.get(event_type)
            if not http_method:
                self.tracer.error("select_method",
                    Exception(f"Tipo não suportado: {event_type}"),
                    {"event_type": event_type}
                )
                return {"status": "error", "message": f"Tipo de evento não suportado: '{event_type}'"}

            # 4. Construir URL (DELETE usa ID no path)
            external_id = payload["data"].get("externalId", "")
            url = f"{self.api_url}/{external_id}" if http_method == "DELETE" else self.api_url

            self.tracer.success("prepare_request", {
                "url": url,
                "method": http_method,
                "external_id": external_id,
            })

            # 5. Chamada HTTP (com rastreamento simplificado)
            async with httpx.AsyncClient(timeout=30.0) as client:
                if http_method == "DELETE":
                    response = await client.delete(url, auth=(self.api_username, self.api_password))
                else:
                    response = await client.request(
                        http_method, url,
                        json=payload.get("data"),
                        auth=(self.api_username, self.api_password),
                    )

            if not response.is_success:
                logger.error(f"[MeuSistema] {http_method} {url} → {response.status_code}")
                self.tracer.error("post_to_meusistema",
                    Exception(f"HTTP {response.status_code}"),
                    {
                        "url": url,
                        "method": http_method,
                        "status_code": response.status_code,
                        "response_body": response.text[:500],
                    }
                )
                return {
                    "status":          "error",
                    "message":         f"API retornou status {response.status_code}",
                    "api_url":         url,
                    "http_method":     http_method,
                    "api_status_code": response.status_code,
                    "api_response":    response.text,
                }

            # Sucesso!
            self.tracer.success("post_to_meusistema", {
                "url": url,
                "method": http_method,
                "status_code": response.status_code,
                "response_size": len(response.content),
            })

            return {
                "status":          "success",
                "message":         f"Evento enviado ao MeuSistema [{http_method}]",
                "api_url":         url,
                "http_method":     http_method,
                "api_status_code": response.status_code,
            }

        except httpx.RequestError as exc:
            logger.error(f"[MeuSistema] Falha de rede: {exc}")
            self.tracer.error("post_to_meusistema", exc, {
                "error_type": type(exc).__name__,
            })
            return {"status": "error", "message": f"Falha de rede: {exc}"}

        except Exception as exc:
            logger.error(f"[MeuSistema] Erro inesperado: {exc}")
            self.tracer.error("process", exc, {})
            return {"status": "error", "message": f"Erro: {exc}"}

    async def health_check(self) -> bool:
        return bool(self.api_url and self.api_username and self.api_password)`}
                </CodeBlock>
              </Section>
            </div>

            {/* Footer */}
            <div className="border-t border-[#2d2d2d] pt-6 mt-4 flex items-center gap-3">
              <div className="w-6 h-6 rounded bg-[#0078d4] flex items-center justify-center flex-shrink-0">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                </svg>
              </div>
              <p className="text-xs text-[#555]">
                SolidFlow / Hub de Integrações — Guia do Desenvolvedor v1.0
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}
